import React, { useState, useEffect } from 'react';
import { userAPI, getUploadUrl } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { UserCheck, Check, X, Eye, FileText, ImageIcon, AlertCircle, Clock, Shield } from 'lucide-react';

const AdminKYC = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      const [pendingRes, approvedRes, rejectedRes] = await Promise.all([
        userAPI.getUsers({ kyc_status: 'pending' }),
        userAPI.getUsers({ kyc_status: 'approved' }),
        userAPI.getUsers({ kyc_status: 'rejected' }),
      ]);
      // V4.18.45 — Include users with the new `kyc_documents[]` array
      // shape (mobile V4.18.40 uploads) alongside the legacy single
      // `kyc_document` string field. Without this, mobile-registered
      // users never surface on the Pending review tab.
      const pendingReview = (pendingRes.data || []).filter(u =>
        u.kyc_document || (Array.isArray(u.kyc_documents) && u.kyc_documents.length > 0)
      );
      const approved = approvedRes.data || [];
      const rejected = rejectedRes.data || [];
      setUsers([...pendingReview, ...rejected, ...approved]);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await userAPI.updateKYCStatus(userId, 'approved');
      toast.success('KYC approved');
      loadUsers();
      setSelectedUser(null);
    } catch (error) {
      toast.error('Failed to approve KYC');
    }
  };

  const handleReject = async (userId) => {
    try {
      await userAPI.updateKYCStatus(userId, 'rejected');
      toast.success('KYC rejected');
      loadUsers();
      setSelectedUser(null);
    } catch (error) {
      toast.error('Failed to reject KYC');
    }
  };

  const getStatusBadge = (status, hasDoc) => {
    const styles = {
      approved: 'bg-green-100 text-green-700 border-green-200',
      pending: hasDoc ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-gray-100 text-gray-500 border-gray-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
    };
    const labels = {
      approved: 'Approved',
      pending: hasDoc ? 'Pending Review' : 'Not Submitted',
      rejected: 'Rejected',
    };
    return (
      <Badge variant="outline" className={styles[status] || 'bg-gray-100 text-gray-700'}>
        {labels[status] || status}
      </Badge>
    );
  };

  // Resolve KYC document URL — accepts:
  //   • null/undefined         → returns null
  //   • legacy base64 string   → returns as-is
  //   • relative path string   → prefixes /api/uploads via getUploadUrl
  //   • absolute URL string    → returns as-is
  //   • array of doc entries   → picks the first entry's file_url/url/data string
  //   • object with file_url/url → picks that string
  // V4.18.45 — Defensive: crashed the whole KYC page whenever a user's
  // kyc_document was written by the mobile app as a `{file_id,file_url}`
  // object or by the new /kyc-documents endpoint as an ARRAY, because
  // .startsWith() only exists on strings.
  const getDocumentUrl = (doc) => {
    if (!doc) return null;
    // Array — pick the first entry with a resolvable url.
    if (Array.isArray(doc)) {
      const first = doc.find(d => d && (d.file_url || d.url || typeof d === 'string')) || doc[0];
      return getDocumentUrl(first);
    }
    // Object — try common url keys.
    if (typeof doc === 'object') {
      return getDocumentUrl(doc.file_url || doc.url || doc.path || null);
    }
    // String — legacy base64 or file-path.
    if (typeof doc !== 'string') return null;
    if (doc.startsWith('data:')) return doc;
    if (doc.startsWith('/uploads/') || doc.startsWith('/api/uploads/')) return getUploadUrl(doc);
    return doc;
  };

  // V4.18.45 — Accept both legacy `kyc_document` (single string/object) and
  // new `kyc_documents` (array from mobile /kyc-documents endpoint).
  const hasKycDocument = (u) =>
    !!u?.kyc_document || (Array.isArray(u?.kyc_documents) && u.kyc_documents.length > 0);
  const primaryKycDocument = (u) =>
    u?.kyc_document || (Array.isArray(u?.kyc_documents) ? u.kyc_documents : null);

  const isPending = (user) => user?.kyc_status === 'pending' && hasKycDocument(user);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#4E50FF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="admin-kyc">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
          <UserCheck className="h-6 w-6 text-[#4E50FF]" />
          KYC Approvals
        </h1>
        <p className="text-sm text-slate-500 mt-1">Review and approve user verifications</p>
      </div>

      {users.length > 0 ? (
        <Card className="border shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-slate-600">User</TableHead>
                <TableHead className="text-slate-600">Role</TableHead>
                <TableHead className="text-slate-600">IC Number</TableHead>
                <TableHead className="text-slate-600">IC Document</TableHead>
                <TableHead className="text-slate-600">Status</TableHead>
                <TableHead className="text-slate-600">Submitted</TableHead>
                <TableHead className="text-slate-600">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} data-testid={`kyc-row-${user.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#4E50FF]/10 flex items-center justify-center">
                        <span className="text-[#4E50FF] font-semibold text-sm">
                          {user.full_name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{user.full_name}</p>
                        <p className="text-sm text-slate-500">{user.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-700 capitalize">{user.role}</TableCell>
                  <TableCell className="text-slate-700 font-mono">{user.ic_number || '-'}</TableCell>
                  <TableCell>
                    {hasKycDocument(user) ? (
                      <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">
                        <ImageIcon className="w-3 h-3 mr-1" /> Uploaded
                      </Badge>
                    ) : (
                      <span className="text-gray-400 text-sm">No document</span>
                    )}
                  </TableCell>
                  <TableCell>{getStatusBadge(user.kyc_status, !!user.kyc_document)}</TableCell>
                  <TableCell className="text-slate-500">{formatDate(user.kyc_submitted_at || user.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedUser(user)}
                        data-testid={`view-kyc-${user.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" /> View
                      </Button>
                      {isPending(user) && (
                        <>
                          <Button size="sm" onClick={() => handleApprove(user.id)} className="bg-green-500 hover:bg-green-600 text-white" data-testid={`approve-kyc-${user.id}`}>
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleReject(user.id)} className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`reject-kyc-${user.id}`}>
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </Card>
      ) : (
        <Card className="text-center py-12 border shadow-sm">
          <CardContent>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserCheck className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Pending KYC</h3>
            <p className="text-slate-500">All KYC requests have been processed</p>
          </CardContent>
        </Card>
      )}

      {/* KYC Document Review Dialog */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="kyc-review-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#4E50FF]" />
              KYC Document Review
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 mt-4">
              {/* Status indicator */}
              <div className="flex items-center justify-between">
                {getStatusBadge(selectedUser.kyc_status, hasKycDocument(selectedUser))}
                {selectedUser.kyc_approved_at && (
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Approved {formatDate(selectedUser.kyc_approved_at)}
                  </span>
                )}
                {selectedUser.kyc_rejected_at && (
                  <span className="text-xs text-red-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Rejected {formatDate(selectedUser.kyc_rejected_at)}
                  </span>
                )}
              </div>

              {/* User details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Full Name</p>
                  <p className="text-slate-900 font-medium">{selectedUser.full_name}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">IC Number</p>
                  <p className="text-slate-900 font-mono">{selectedUser.ic_number || <span className="text-gray-400 italic">Not provided</span>}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="text-slate-900">{selectedUser.email}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Phone</p>
                  <p className="text-slate-900">{selectedUser.phone || '-'}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Bank Name</p>
                  <p className="text-slate-900">{selectedUser.bank_name || '-'}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Bank Account</p>
                  <p className="text-slate-900 font-mono">{selectedUser.bank_account || '-'}</p>
                </div>
              </div>

              {/* IC Document Image */}
              {hasKycDocument(selectedUser) ? (
                <div className="border rounded-lg overflow-hidden" data-testid="kyc-document-section">
                  <div className="flex items-center justify-between p-3 bg-slate-50 border-b">
                    <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <Shield className="w-4 h-4 text-blue-600" /> IC Document / KYC Proof
                    </p>
                    <a
                      href={getDocumentUrl(primaryKycDocument(selectedUser))}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Open in new tab
                    </a>
                  </div>
                  <div className="p-4 bg-white flex justify-center">
                    <img
                      src={getDocumentUrl(primaryKycDocument(selectedUser))}
                      alt="KYC Document - IC Copy"
                      className="max-w-full max-h-[400px] h-auto rounded-lg border shadow-sm"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                      data-testid="kyc-document-image"
                    />
                    <div className="hidden items-center justify-center p-8 text-gray-400 flex-col gap-2">
                      <AlertCircle className="w-8 h-8" />
                      <p className="text-sm">Unable to load document image</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border rounded-lg p-8 text-center bg-gray-50" data-testid="kyc-no-document">
                  <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No IC document uploaded</p>
                  <p className="text-xs text-gray-400 mt-1">The user has not submitted an IC copy for verification</p>
                </div>
              )}

              {/* Action buttons - ONLY for pending status */}
              {isPending(selectedUser) ? (
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => handleReject(selectedUser.id)}
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    data-testid="dialog-reject-btn"
                  >
                    <X className="w-4 h-4 mr-2" /> Reject
                  </Button>
                  <Button
                    onClick={() => handleApprove(selectedUser.id)}
                    className="bg-green-500 hover:bg-green-600 text-white"
                    data-testid="dialog-approve-btn"
                  >
                    <Check className="w-4 h-4 mr-2" /> Approve
                  </Button>
                </div>
              ) : selectedUser.kyc_status === 'approved' ? (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <Check className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-green-700">KYC Approved</span>
                  {selectedUser.kyc_approved_at && (
                    <span className="text-xs text-green-500 ml-auto">{formatDate(selectedUser.kyc_approved_at)}</span>
                  )}
                </div>
              ) : selectedUser.kyc_status === 'rejected' ? (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <X className="w-5 h-5 text-red-600" />
                  <span className="text-sm font-medium text-red-700">KYC Rejected</span>
                  {selectedUser.kyc_rejected_at && (
                    <span className="text-xs text-red-500 ml-auto">{formatDate(selectedUser.kyc_rejected_at)}</span>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminKYC;
